import { createClient } from "./httpClientFactory"
import ResponseTransformer from "@/transformers/response.transformer"
import {
  getAuthorizationConfig,
  getMultipartFormdataConfig,
  getPatchContentTypeConfig,
  toIri
} from "@/services/helper"

import { useAuthentication } from "@/composables/useAuthentication"
import { localStorageService } from "@/services/localStorage.service.js"

const httpClient = createClient(import.meta.env.VITE_API_SERVER_URL)
function getSuccessHandler({ transformer = null, transformerMethod }) {
  if (transformer) {
    return (res) => {
      if (transformerMethod) {
        if (res.data["hydra:member"] && res.data["hydra:totalItems"] >= 0) {
          let items = transformer[transformerMethod](res.data["hydra:member"])
          let totalItems = res.data["hydra:totalItems"]
          return { items, totalItems }
        }

        return transformer[transformerMethod](res.data)
      }

      return transformer["fetch"](res.data)
    }
  }

  return (res) => {
    return Promise.resolve(res.data)
  }
}

/**
 * Gets the error handler based on the transformer existence.
 */
function getErrorHandler(error, { transformer = null } = {}) {
  if (transformer && transformer.error) {
    return Promise.reject(transformer["error"](error))
  }

  return Promise.reject(ResponseTransformer.error(error))
}

/**
 * Generates request config based on options object.
 *
 * @param {Object} options Custom options
 * @returns Request config object
 */
export function getConfig(options) {
  const headerConfig = {
    patch: getPatchContentTypeConfig(options.needsAuth),
    multipart: getMultipartFormdataConfig(options.needsAuth),
    default: {
      headers: { "Content-Type": "application/json" },
      ...(options.needsAuth ? getAuthorizationConfig() : {})
    }
  }[["multipart", "patch"].includes(options.contentType) ? options.contentType : "default"]

  if (!localStorageService.getUserAccountIri()) {
    console.warn("User account IRI not found")
  }

  return { ...headerConfig, responseType: options.responseType }
}

/**
 * Merges default options with custom ones.
 *
 * @param {Object} options Custom options
 * @returns The merged options object
 */
function mergeOptions(options) {
  return {
    needsAuth: false,
    contentType: "json",
    ...options,
    config: getConfig(options)
  }
}

function setInterceptor(client) {
  const { userIsAuthenticated } = useAuthentication()

  client.interceptors.request.use(async function (config) {
    // Do something before request is sent
    if (config.headers.Authorization) {
      const _userIsAuthenticated = await userIsAuthenticated()

      if (_userIsAuthenticated) {
        const { headers } = getAuthorizationConfig(config)

        const headerNewAuth = { ...config.headers, Authorization: headers.Authorization }
        return { ...config, headers: headerNewAuth }
      }
    }

    return config
  })
}
class AbstractService {
  constructor({ baseUri, transformer, methodWithoutAuth, client }) {
    this.baseUri = baseUri
    this.transformer = transformer
    this.methodWithoutAuth = methodWithoutAuth
    this.httpClient = client || httpClient
    setInterceptor(this.httpClient)
  }

  ///////////////////////////////////////////////////////////////

  create(options = {}) {
    options = mergeOptions({
      transformer: this.transformer,
      needsAuth: this.methodWithoutAuth
        ? typeof this.methodWithoutAuth === "string" && this.methodWithoutAuth === "all"
          ? false
          : !this.methodWithoutAuth.includes("create")
        : true,
      ...options
    })

    return this.httpClient
      .post(this.baseUri, options.payload, options.config)
      .then(getSuccessHandler(options))
      .catch((error) => getErrorHandler(error, options))
  }

  getItem(uuid) {
    if (!uuid) return
    const options = mergeOptions({
      transformer: this.transformer,
      needsAuth: this.methodWithoutAuth
        ? typeof this.methodWithoutAuth === "string" && this.methodWithoutAuth === "all"
          ? false
          : !this.methodWithoutAuth.includes("getItem")
        : true
    })
    return this.httpClient
      .get(toIri(this.baseUri, uuid), options.config)
      .then(getSuccessHandler(options))
      .catch((error) => getErrorHandler(error, options))
  }

  getCollection(customUrl, options = {}) {
    let url = customUrl ?? this.baseUri
    options = mergeOptions({
      transformer: this.transformer,
      transformerMethod: this.transformer ? "receiveCollection" : null,
      needsAuth: this.methodWithoutAuth
        ? typeof this.methodWithoutAuth === "string" && this.methodWithoutAuth === "all"
          ? false
          : !this.methodWithoutAuth.includes("getCollection")
        : true,
      ...options
    })

    if (options.queryString) {
      url = `${url}?${options.queryString}`
    }

    return this.httpClient
      .get(url, options.config)
      .then(getSuccessHandler(options))
      .catch((error) => getErrorHandler(error, options))
  }

  update(uuid, options) {
    options = mergeOptions({
      contentType: "patch",
      transformer: this.transformer,
      needsAuth: this.methodWithoutAuth
        ? typeof this.methodWithoutAuth === "string" && this.methodWithoutAuth === "all"
          ? false
          : !this.methodWithoutAuth.includes("update")
        : true,
      ...options
    })

    return httpClient
      .patch(toIri(this.baseUri, uuid), options.payload, options.config)
      .then(getSuccessHandler(options))
      .catch((error) => getErrorHandler(error, options))
  }

  deleteItem(uuid) {
    const options = mergeOptions({
      transformer: this.transformer,
      needsAuth: this.methodWithoutAuth
        ? typeof this.methodWithoutAuth === "string" && this.methodWithoutAuth === "all"
          ? false
          : !this.methodWithoutAuth.includes("deleteItem")
        : true
    })

    return this.httpClient.delete(toIri(this.baseUri, uuid), options.config).catch(getErrorHandler)
  }

  ///////////////////////////////////////////////////////////////

  /**
   * Performs a GET request.
   *
   * @param {String} url Resource's base URL
   * @param {Object} options Options object
   */
  get(url, options = {}) {
    options = mergeOptions(options)

    return this.httpClient
      .get(url, options.config)
      .then(getSuccessHandler(options))
      .catch((error) => getErrorHandler(error, options))
  }

  /**
   * Performs a POST request.
   *
   * @param {String} url Resource's base URL
   * @param {Object} options Options object
   */
  post(url, options) {
    options = mergeOptions(options)

    return this.httpClient
      .post(url, options.payload, options.config)
      .then(getSuccessHandler(options))
      .catch((error) => getErrorHandler(error, options))
  }

  /**
   * Performs a PATCH request.
   *
   * @param {String} url Resource's base URL
   * @param {Object} options Options object
   */
  patch(url, options) {
    options = mergeOptions({ contentType: "patch", ...options })
    return this.httpClient
      .patch(url, options.payload, options.config)
      .then(getSuccessHandler(options))
      .catch((error) => getErrorHandler(error, options))
  }

  /**
   * Performs a DELETE request.
   *
   * @param {String} url Resource's base URL
   * @param {Object} options Options object
   */
  delete(url, options = {}) {
    options = mergeOptions(options)
    return this.httpClient
      .delete(url, options.config)
      .catch((error) => getErrorHandler(error, options))
  }
}

export default AbstractService
